import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { emailShareHref, whatsappShareHref, qrFileName } from "./utils/shareLink";
import "./ShareQr.css";

// Share tab: a QR code of the form's live link (made in the browser), a
// 1024 px PNG download with the title under the code, Copy link, and sharing
// by email or WhatsApp. `link` is exactly the link the Share tab shows.

const DOWNLOAD_WIDTH = 1024;
const TITLE_FONT = '600 44px "Segoe UI", Roboto, Arial, sans-serif';

// Split the title into lines that fit `maxWidth` (at most 3; the last one
// ends with "..." if it had to be cut).
function wrapTitle(ctx, title, maxWidth) {
    const words = String(title).split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";
    for (const word of words) {
        const next = line ? `${line} ${word}` : word;
        if (ctx.measureText(next).width <= maxWidth) { line = next; continue; }
        if (line) lines.push(line);
        line = word;
    }
    if (line) lines.push(line);
    if (lines.length > 3) {
        const kept = lines.slice(0, 3);
        let last = kept[2];
        while (last && ctx.measureText(`${last}...`).width > maxWidth) last = last.slice(0, -1);
        kept[2] = `${last}...`;
        return kept;
    }
    return lines;
}

// The download: the QR code 1024 px wide, the title printed under it.
async function buildDownload(link, title) {
    const qr = document.createElement("canvas");
    await QRCode.toCanvas(qr, link, { width: DOWNLOAD_WIDTH, margin: 4, errorCorrectionLevel: "M", color: { dark: "#000000", light: "#ffffff" } });
    const measure = document.createElement("canvas").getContext("2d");
    measure.font = TITLE_FONT;
    const lines = wrapTitle(measure, title || "Form", DOWNLOAD_WIDTH - 120);
    const lineHeight = 58;
    const out = document.createElement("canvas");
    out.width = DOWNLOAD_WIDTH;
    out.height = DOWNLOAD_WIDTH + lines.length * lineHeight + 60;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(qr, 0, 0);
    ctx.fillStyle = "#111827";
    ctx.font = TITLE_FONT;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    lines.forEach((text, i) => ctx.fillText(text, DOWNLOAD_WIDTH / 2, DOWNLOAD_WIDTH + 10 + i * lineHeight));
    return out;
}

export default function ShareQr({ link, title, onCopied }) {
    const previewRef = useRef(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!link || !previewRef.current) return;
        QRCode.toCanvas(previewRef.current, link, { width: 200, margin: 2, errorCorrectionLevel: "M" })
            .then(() => setError(null))
            .catch(() => setError("Could not make the QR code."));
    }, [link]);

    const download = async () => {
        setBusy(true);
        try {
            const canvas = await buildDownload(link, title);
            const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = qrFileName(title);
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(a.href), 5000);
        } catch (e) {
            setError("Could not make the PNG.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            if (onCopied) onCopied();
        } catch (e) {
            setError("Could not copy the link; select it above and copy it.");
        }
    };

    return (
        <div className="share-qr-card">
            <div className="share-qr-code">
                <canvas ref={previewRef} aria-label={`QR code for ${link}`} />
            </div>
            <div className="share-qr-side">
                <h3 className="share-qr-title">Share with a QR code</h3>
                <p className="share-qr-text">People can scan it with their phone camera to open the form.</p>
                <div className="share-qr-actions">
                    <button type="button" className="share-qr-btn share-qr-btn-primary" onClick={download} disabled={busy || !link}>
                        <i className="fa-solid fa-download"></i> Download PNG
                    </button>
                    <button type="button" className="share-qr-btn" onClick={copy} disabled={!link}>
                        <i className="fa-regular fa-copy"></i> Copy link
                    </button>
                </div>
                <div className="share-qr-actions">
                    <a className="share-qr-btn" href={emailShareHref(title, link)}>
                        <i className="fa-regular fa-envelope"></i> Email
                    </a>
                    <a className="share-qr-btn" href={whatsappShareHref(title, link)} target="_blank" rel="noopener noreferrer">
                        <i className="fa-brands fa-whatsapp"></i> WhatsApp
                    </a>
                </div>
                {error && <p className="share-qr-error">{error}</p>}
            </div>
        </div>
    );
}
